/**
 * /cobranca/boletos — Cobranças multi-meio (Boleto / PIX / Cartão).
 *
 * Redesign FIN-10: casca .fin2 fiel ao mockup Financeiro V2.2 (#cobranca).
 * breadcrumb + page-title + kpi-strip-v2 + tabchips + dtable + slide-over de
 * nova cobrança + painel de ações por linha (meio-específico: boleto/PIX/cartão
 * + marcar pago / cancelar). Consome o backend de cobrança existente (M5/M13)
 * — sem mudar lógica.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiCheckCircle, FiExternalLink, FiPlus, FiSettings, FiX } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import ClienteSelect from "../../../components/ClienteSelect";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  cancelarBoleto, criarBoleto, listarBoletos, marcarBoletoPago,
  SITUACAO_BOLETO_LABELS, SITUACAO_BOLETO_TONES,
} from "../../../services/cobranca";
import { formatarReais, listarLancamentos } from "../../../services/financeiro";
import { listarPedidos } from "../../../services/pedidos";
import { PageContainer } from "../../../styles/shared";
import { Input, Select } from "../../Orcamentos/styles";

const MEIOS = [
  { value: "BOLETO", label: "Boleto bancário", tone: "neutral", hint: "Boleto tradicional — código de barras + linha digitável + PDF." },
  { value: "PIX", label: "PIX", tone: "success", hint: "QR Code + copia-e-cola gerados automaticamente — recebimento instantâneo." },
  { value: "CARTAO", label: "Cartão", tone: "info", hint: "Link de checkout hospedado pelo Asaas (sem dados de cartão no PharuERP)." },
];
const MEIO_TONE = Object.fromEntries(MEIOS.map((m) => [m.value, m.tone]));

// CRIT-8 (auditoria 2026-08-07): a cobrança precisa nascer amarrada à parcela
// do pedido — é o `lancamento_id` que faz o pagamento baixar a conta a receber
// quando o webhook do gateway confirma. Antes o vínculo era um texto digitado
// à mão na descrição ("Pedido PV-UN1-12") e nada baixava.
const SITUACOES_COBRAVEIS = ["RECEBIDO", "EM_PRODUCAO", "EM_EXPEDICAO", "ENTREGUE"];

const TABS = [
  { id: "todos", label: "Todos", match: () => true },
  { id: "pendentes", label: "Em aberto", match: (b) => b.situacao === "PENDENTE" && !b.esta_vencido },
  { id: "pagos", label: "Pagos", match: (b) => b.situacao === "PAGO" },
  { id: "vencidos", label: "Vencidos", match: (b) => b.esta_vencido },
  { id: "cancelados", label: "Cancelados", match: (b) => b.situacao === "CANCELADO" },
];

function hojeISO() { return new Date().toISOString().slice(0, 10); }
function maisSeteDiasISO() {
  const d = new Date(); d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}
function ddmmaa(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}
function statusDisplay(b) {
  if (b.esta_vencido) return { label: "Vencido", tone: "danger" };
  return { label: SITUACAO_BOLETO_LABELS[b.situacao] || b.situacao, tone: SITUACAO_BOLETO_TONES[b.situacao] || "neutral" };
}

function NovaCobrancaForm({ request, onSalvar, onCancel }) {
  // V39: antes chegava a base inteira de clientes por prop, so para
  // encher um <select>. Agora o proprio seletor busca no servidor.
  const [clienteSel, setClienteSel] = useState(null);
  const [d, setD] = useState({
    pedido_id: "", lancamento_id: "", cliente_id: "", valor: "",
    vencimento: maisSeteDiasISO(), descricao: "", meio_pagamento: "BOLETO",
  });
  const [pedidos, setPedidos] = useState([]);
  const [parcelas, setParcelas] = useState([]);
  const [carregandoParcelas, setCarregandoParcelas] = useState(false);
  // Revisão adversarial da Fase 4: nada impedia N cobranças ativas para a
  // mesma parcela. Dividir a parcela em dois instrumentos e reemitir uma
  // cobrança perdida são legítimos — por isso a defesa é confirmação
  // explícita, não bloqueio.
  const [confirmaDuplicidade, setConfirmaDuplicidade] = useState(false);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const meioHint = MEIOS.find((m) => m.value === d.meio_pagamento)?.hint;

  const parcelaSel = useMemo(
    () => parcelas.find((p) => String(p.id) === String(d.lancamento_id)) || null,
    [parcelas, d.lancamento_id],
  );
  const duplicando = !!parcelaSel?.tem_cobranca_ativa;
  const valido = d.cliente_id && Number(d.valor) > 0 && d.vencimento
    && (!duplicando || confirmaDuplicidade);

  // Pedidos cobráveis (o backend filtra 1 situação por request).
  useEffect(() => {
    let vivo = true;
    (async () => {
      const todos = [];
      for (const situacao of SITUACOES_COBRAVEIS) {
        try {
          const p = await listarPedidos(request, { situacao, pageSize: 200 });
          todos.push(...(Array.isArray(p) ? p : p?.results || []));
        } catch {
          // O `request` do AuthContext já mostra o toast da falha; aqui só
          // impedimos que uma situação indisponível aborte as outras — e a
          // cobrança avulsa continua possível mesmo sem lista de pedidos.
        }
      }
      if (vivo) setPedidos(todos);
    })();
    return () => { vivo = false; };
  }, [request]);

  const pedidoSel = useMemo(
    () => pedidos.find((p) => String(p.id) === String(d.pedido_id)) || null,
    [pedidos, d.pedido_id],
  );

  // Ao escolher o pedido: cliente vem dele (e trava) + carrega as parcelas
  // em aberto (ENTRADA não confirmada) para amarrar a cobrança a uma delas.
  useEffect(() => {
    let vivo = true;
    if (!d.pedido_id) { setParcelas([]); return undefined; }
    setCarregandoParcelas(true);
    (async () => {
      try {
        const p = await listarLancamentos(request, {
          pedido: d.pedido_id, tipo: "ENTRADA", confirmado: false,
        });
        if (vivo) setParcelas(Array.isArray(p) ? p : p?.results || []);
      } catch {
        // Toast da falha vem do `request`; aqui apenas caímos no estado
        // "sem parcela em aberto", que o hint abaixo explica ao usuário.
        if (vivo) setParcelas([]);
      } finally {
        if (vivo) setCarregandoParcelas(false);
      }
    })();
    return () => { vivo = false; };
  }, [request, d.pedido_id]);

  const escolherPedido = (id) => {
    const p = pedidos.find((x) => String(x.id) === String(id));
    setD((prev) => ({
      ...prev,
      pedido_id: id,
      lancamento_id: "",
      cliente_id: id ? (p?.cliente || "") : prev.cliente_id,
      descricao: prev.descricao || (p ? `Pedido ${p.numero}` : ""),
    }));
  };

  const escolherParcela = (id) => {
    const parcela = parcelas.find((x) => String(x.id) === String(id));
    setConfirmaDuplicidade(false);  // trocou de parcela = confirma de novo
    setD((prev) => ({
      ...prev,
      lancamento_id: id,
      valor: parcela ? String(parcela.valor) : prev.valor,
      vencimento: parcela?.data_competencia || prev.vencimento,
      descricao: parcela?.descricao || prev.descricao,
    }));
  };

  // O pedido manda no cliente: escolher pedido descarta a escolha manual,
  // senao o id antigo continuaria no payload por tras do campo travado.
  useEffect(() => {
    if (d.pedido_id && clienteSel) setClienteSel(null);
  }, [d.pedido_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const vencimentoNoPassado = d.vencimento && d.vencimento < hojeISO();

  const submit = async () => {
    await onSalvar({
      cliente_id: d.cliente_id, valor: parseFloat(d.valor),
      vencimento: d.vencimento, descricao: d.descricao.trim(),
      meio_pagamento: d.meio_pagamento || "BOLETO",
      pedido_id: d.pedido_id || undefined,
      lancamento_id: d.lancamento_id || undefined,
      // O backend recusa (409) a 2ª cobrança da mesma parcela sem esta flag —
      // a UI só a envia depois do usuário marcar a confirmação.
      permitir_duplicidade: duplicando && confirmaDuplicidade ? true : undefined,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Meio de pagamento *</span>
        <Select value={d.meio_pagamento} onChange={(e) => set("meio_pagamento", e.target.value)}>
          {MEIOS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </Select>
        <span style={{ fontSize: 11, opacity: 0.65 }}>{meioHint}</span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Pedido</span>
        <Select value={d.pedido_id} onChange={(e) => escolherPedido(e.target.value)}>
          <option value="">— Cobrança avulsa (sem pedido) —</option>
          {pedidos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.numero} · {p.cliente_nome || "cliente"} ({p.situacao_display || p.situacao})
            </option>
          ))}
        </Select>
        <span style={{ fontSize: 11, opacity: 0.65 }}>
          Amarrar ao pedido é o que permite responder “quais cobranças são deste pedido”.
        </span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Cliente *</span>
        {pedidoSel ? (
          <>
            <Select value={d.cliente_id} disabled>
              <option value={d.cliente_id}>{pedidoSel.cliente_nome || "Cliente do pedido"}</option>
            </Select>
            <span style={{ fontSize: 11, opacity: 0.65 }}>
              Definido pelo pedido {pedidoSel.numero}. Volte a “Cobrança avulsa” para escolher outro cliente.
            </span>
          </>
        ) : (
          <ClienteSelect
            request={request}
            value={clienteSel}
            onChange={(c) => {
              setClienteSel(c);
              set("cliente_id", c?.id || "");
            }}
          />
        )}
      </label>

      {d.pedido_id && (
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Parcela a receber</span>
          <Select value={d.lancamento_id} disabled={carregandoParcelas || parcelas.length === 0}
                  onChange={(e) => escolherParcela(e.target.value)}>
            <option value="">
              {carregandoParcelas
                ? "Carregando parcelas…"
                : parcelas.length
                  ? "— Escolha a parcela —"
                  : "Nenhuma parcela em aberto"}
            </option>
            {parcelas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.tem_cobranca_ativa ? "⚠ já tem cobrança · " : ""}
                {p.descricao} · {formatarReais(p.valor)} · comp. {ddmmaa(p.data_competencia)}
              </option>
            ))}
          </Select>
          <span style={{ fontSize: 11, opacity: 0.65 }}>
            {parcelas.length === 0 && !carregandoParcelas
              ? "Este pedido não tem parcela em aberto — a cobrança sai avulsa e o pagamento NÃO baixa nenhuma conta a receber automaticamente."
              : d.lancamento_id
                ? "Quando o gateway confirmar o pagamento, esta parcela é baixada em Contas a Receber."
                : "Sem parcela escolhida, o pagamento não baixa nada em Contas a Receber."}
          </span>
        </label>
      )}

      {duplicando && (
        <div style={{
          padding: "10px 12px", background: "var(--warning-bg)",
          border: "1px solid var(--warning-border)", borderRadius: 8,
          fontSize: 12.5, color: "var(--ink-2)",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div>
            <FiAlertTriangle size={13} aria-hidden="true" />{" "}
            <strong>Esta parcela já tem cobrança ativa.</strong> Gerar outra só
            faz sentido se você está dividindo a parcela em dois instrumentos
            (ajuste o valor abaixo) ou reemitindo uma cobrança que o cliente
            perdeu. Se a anterior não vale mais, cancele-a em vez de duplicar:
            se o cliente pagar as duas, a segunda entra como paga mas <strong>não
            baixa nada</strong> em Contas a Receber.
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={confirmaDuplicidade}
                   onChange={(e) => setConfirmaDuplicidade(e.target.checked)} />
            <span>Entendi — quero mesmo uma segunda cobrança para esta parcela.</span>
          </label>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Valor *</span>
          <Input type="number" step="0.01" min="0.01" value={d.valor}
                 onChange={(e) => set("valor", e.target.value)} placeholder="0,00" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Vencimento *</span>
          <Input type="date" value={d.vencimento} onChange={(e) => set("vencimento", e.target.value)} />
        </label>
      </div>
      {vencimentoNoPassado && (
        <span style={{ fontSize: 11, color: "var(--warning-text)" }}>
          O gateway recusa vencimento no passado — ajuste a data antes de gerar.
        </span>
      )}

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Descrição</span>
        <Input value={d.descricao} maxLength={300} onChange={(e) => set("descricao", e.target.value)}
               placeholder="Texto que o cliente vê na cobrança" />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Cancelar</button>
        <AsyncButton variant="primary" disabled={!valido} successMessage="Cobrança gerada" onClick={submit}>
          <FiPlus size={14} aria-hidden="true" /> Gerar cobrança
        </AsyncButton>
      </div>
    </div>
  );
}

function CopiaCola({ texto, label }) {
  return (
    <div className="copiacola" title={texto}>
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{texto}</span>
      <button type="button" className="btn btn--secondary btn--sm"
              onClick={() => { navigator.clipboard.writeText(texto); window.alert(`${label} copiado.`); }}>
        Copiar
      </button>
    </div>
  );
}

function AcoesCobranca({ boleto, podeGerar, podeCancelar, onMarcarPago, onCancelar }) {
  const meio = boleto.meio_pagamento || "BOLETO";
  const st = statusDisplay(boleto);
  // B20 (auditoria 2026-08-07): o payload EMV do PIX é dado de pagamento e
  // NUNCA vai a serviço de terceiro (api.qrserver.com) — o QR vem do PNG que
  // o próprio gateway entrega; sem ele, mostra-se só o copia-e-cola.
  const qrSrc = boleto.pix_qrcode_base64
    ? `data:image/png;base64,${boleto.pix_qrcode_base64}`
    : null;

  return (
    <div className="fin2-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="ld-amount">
        <div>
          <div style={{ fontWeight: 600 }}>{boleto.cliente_nome}</div>
          <div className="tiny muted" style={{ marginTop: 2 }}>
            vence {ddmmaa(boleto.vencimento)} · {boleto.provider_display}
            {boleto.pedido_numero ? ` · ${boleto.pedido_numero}` : " · avulsa"}
          </div>
        </div>
        <div className="ld-val">{formatarReais(boleto.valor)}</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span className={"badge no-dot " + (MEIO_TONE[meio] || "neutral")}>{boleto.meio_pagamento_display || meio}</span>
        <span className={"badge no-dot " + st.tone}>{st.label}</span>
      </div>

      {boleto.erro_detalhe && (
        <div style={{ padding: "10px 12px", background: "var(--danger-bg)", border: "1px solid var(--danger-border)", borderRadius: 8, fontSize: 12.5, color: "var(--ink-2)" }}>
          <FiAlertTriangle size={13} aria-hidden="true" /> {boleto.erro_detalhe}
        </div>
      )}

      {meio === "PIX" && (qrSrc || boleto.pix_copia_cola) && (
        <div>
          <div className="so-section-label">QR Code PIX</div>
          {qrSrc && (
            <div className="fiscal-qr" style={{ marginBottom: 10 }}>
              <div className="qr-box"><img src={qrSrc} alt="QR Code PIX" style={{ width: "100%", height: "100%", display: "block" }} /></div>
              <div className="qr-meta">O cliente escaneia o QR ou usa o copia-e-cola abaixo. Recebimento instantâneo.</div>
            </div>
          )}
          {boleto.pix_copia_cola && <CopiaCola texto={boleto.pix_copia_cola} label="Copia-e-cola PIX" />}
        </div>
      )}

      {meio === "BOLETO" && (boleto.linha_digitavel || boleto.codigo_barras) && (
        <div>
          <div className="so-section-label">Boleto bancário</div>
          {boleto.linha_digitavel && <CopiaCola texto={boleto.linha_digitavel} label="Linha digitável" />}
          {boleto.nosso_numero && <div className="tiny muted" style={{ marginTop: 6 }}>Nosso número: <span className="mono">{boleto.nosso_numero}</span></div>}
        </div>
      )}

      {meio === "CARTAO" && boleto.url_boleto && (
        <div>
          <div className="so-section-label">Checkout do cartão</div>
          <div className="tiny muted" style={{ marginBottom: 8 }}>Checkout hospedado pelo Asaas — o PharuERP não captura dados de cartão.</div>
          <CopiaCola texto={boleto.url_boleto} label="Link de checkout" />
        </div>
      )}

      {boleto.motivo_cancelamento && (
        <div className="tiny muted">Motivo do cancelamento: {boleto.motivo_cancelamento}</div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {boleto.url_boleto && (
          <a className="btn btn--secondary" href={boleto.url_boleto} target="_blank" rel="noreferrer">
            <FiExternalLink size={14} aria-hidden="true" />
            {meio === "CARTAO" ? " Abrir checkout" : meio === "PIX" ? " Abrir página" : " Abrir boleto"}
          </a>
        )}
        {boleto.situacao === "PENDENTE" && podeGerar && (
          <AsyncButton variant="primary" successMessage="Marcado como pago" onClick={() => onMarcarPago(boleto)}>
            <FiCheckCircle size={14} aria-hidden="true" /> Marcar pago
          </AsyncButton>
        )}
        {boleto.pode_cancelar && podeCancelar && (
          <AsyncButton variant="danger" silentError onClick={() => onCancelar(boleto)}>
            <FiX size={14} aria-hidden="true" /> Cancelar
          </AsyncButton>
        )}
      </div>
    </div>
  );
}

export default function BoletosLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("todos");
  const [novoAberto, setNovoAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null);

  const podeGerar = isMaster || hasAllPermissions(["cobranca:gerar"]);
  const podeCancelar = isMaster || hasAllPermissions(["cobranca:cancelar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarBoletos(request, {});
      setItems(Array.isArray(p) ? p : p?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar cobranças.");
    } finally { setLoading(false); }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvarNovo = useCallback(async (dados) => {
    await criarBoleto(request, dados);
    setNovoAberto(false);
    await carregar();
  }, [request, carregar]);

  const marcar = useCallback(async (b) => {
    await marcarBoletoPago(request, b.id, { pago_em: hojeISO() });
    setSelecionado(null);
    await carregar();
  }, [request, carregar]);

  const cancelar = useCallback(async (b) => {
    const motivo = window.prompt("Motivo do cancelamento:");
    if (motivo === null) return;
    await cancelarBoleto(request, b.id, motivo);
    setSelecionado(null);
    await carregar();
  }, [request, carregar]);

  const kpis = useMemo(() => {
    const acc = { aberto: 0, abertoN: 0, pago: 0, pagoN: 0, venc: 0, vencN: 0, total: 0, totalN: 0 };
    for (const b of items) {
      const v = Number(b.valor || 0);
      if (b.situacao !== "CANCELADO") { acc.total += v; acc.totalN += 1; }
      if (b.situacao === "PENDENTE") { acc.aberto += v; acc.abertoN += 1; }
      if (b.situacao === "PAGO") { acc.pago += v; acc.pagoN += 1; }
      if (b.esta_vencido) { acc.venc += v; acc.vencN += 1; }
    }
    return acc;
  }, [items]);

  const filtro = TABS.find((t) => t.id === tab) || TABS[0];
  const visiveis = useMemo(() => items.filter(filtro.match), [items, filtro]);

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Cobranças</span>
          </div>
          <h1 className="page-title">Cobranças</h1>
          <div className="page-sub">Boleto, PIX e cartão via gateway — {items.length} cobrança(s) no período</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="btn btn--ghost" onClick={() => navigate("/cobranca/configuracao")}>
            <FiSettings size={14} aria-hidden="true" /> Configuração
          </button>
          {podeGerar && (
            <button type="button" className="btn btn--primary" onClick={() => setNovoAberto(true)}>
              <FiPlus size={14} aria-hidden="true" /> Nova cobrança
            </button>
          )}
        </div>
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">Em aberto</div><div className="val" style={{ color: kpis.aberto > 0 ? "var(--warning-text)" : undefined }}>{formatarReais(kpis.aberto)}</div><div className="meta">{kpis.abertoN} pendente(s)</div></div>
        <div className="kpi-v2"><div className="lbl">Pagas</div><div className="val" style={{ color: "var(--success-text)" }}>{formatarReais(kpis.pago)}</div><div className="meta">{kpis.pagoN} recebida(s)</div></div>
        <div className="kpi-v2"><div className="lbl">Vencidas</div><div className="val" style={{ color: kpis.venc > 0 ? "var(--danger-text)" : undefined }}>{formatarReais(kpis.venc)}</div><div className="meta">{kpis.vencN} cobrança(s)</div></div>
        <div className="kpi-v2"><div className="lbl">Total do período</div><div className="val">{formatarReais(kpis.total)}</div><div className="meta">{kpis.totalN} cobrança(s)</div></div>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      <div className="table-wrap">
        <div className="flex between" style={{ alignItems: "center", padding: "12px 18px", borderBottom: "1px solid var(--border)", gap: 12, flexWrap: "wrap" }}>
          <div className="tabchip-group">
            {TABS.map((t) => (
              <div key={t.id} className={"tabchip" + (tab === t.id ? " active" : "")}
                   role="button" tabIndex={0} onClick={() => setTab(t.id)}
                   onKeyDown={(e) => e.key === "Enter" && setTab(t.id)}>{t.label}</div>
            ))}
          </div>
          <span className="tiny mono muted">{visiveis.length} COBRANÇAS</span>
        </div>
        <table className="dtable">
          <colgroup>
            <col style={{ width: 110 }} /><col /><col style={{ width: 120 }} />
            <col style={{ width: 90 }} /><col style={{ width: 100 }} />
            <col className="col-money" /><col style={{ width: 110 }} />
          </colgroup>
          <thead><tr>
            <th>Número</th><th>Cliente</th><th>Pedido</th><th>Meio</th><th>Vencimento</th>
            <th className="num" style={{ textAlign: "right" }}>Valor</th><th>Status</th>
          </tr></thead>
          <tbody>
            {visiveis.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 0 }}><div className="empty"><div className="msg">
                {loading ? "Carregando…" : "Nenhuma cobrança neste filtro. Gere a primeira em \"Nova cobrança\"."}
              </div></div></td></tr>
            )}
            {visiveis.map((b) => {
              const meio = b.meio_pagamento || "BOLETO";
              const st = statusDisplay(b);
              return (
                <tr key={b.id} className="rowlink" style={b.esta_vencido ? { background: "var(--danger-bg)" } : undefined}
                    onClick={() => setSelecionado(b)}>
                  <td className="mono">{b.numero}</td>
                  <td className="ell" title={b.cliente_nome}>{b.cliente_nome}</td>
                  <td className="mono ell" title={b.pedido_numero || "Cobrança avulsa"}>
                    {b.pedido_numero || "—"}
                  </td>
                  <td><span className={"badge no-dot " + (MEIO_TONE[meio] || "neutral")}>{(b.meio_pagamento_display || meio).split(" ")[0]}</span></td>
                  <td className="mono" style={b.esta_vencido ? { color: "var(--danger-text)" } : undefined}>{ddmmaa(b.vencimento)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{formatarReais(b.valor)}</td>
                  <td><span className={"badge no-dot " + st.tone}>{st.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <SlideOverPanel open={novoAberto} onClose={() => setNovoAberto(false)}
                      title="Nova cobrança" subtitle="Boleto, PIX ou cartão" width={560}>
        {novoAberto && (
          <NovaCobrancaForm request={request}
                            onSalvar={salvarNovo} onCancel={() => setNovoAberto(false)} />
        )}
      </SlideOverPanel>

      <SlideOverPanel open={!!selecionado} onClose={() => setSelecionado(null)}
                      title={selecionado ? `Cobrança ${selecionado.numero}` : ""} width={560}>
        {selecionado && (
          <AcoesCobranca boleto={selecionado} podeGerar={podeGerar} podeCancelar={podeCancelar}
                         onMarcarPago={marcar} onCancelar={cancelar} />
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}
